import React from 'react';
import {defineMessages} from 'react-intl';
import type {DataGridColumn, DataGridServerProps, TextColumn} from '@igp/shared/components/data-grid';
import {DataGridServer} from '@igp/shared/components/data-grid';
import {useNumberFormatter} from '@igp/shared/utils';

import type {PayoutReportColumns, PayoutReportViewModel, PayoutReportViewModelKeys} from '../types';

export const localized = defineMessages<PayoutReportColumns>({
    agent_uid: {
        id: 'payout-report.agent_uid',
        defaultMessage: 'Agent ID',
    },
    agent_username: {
        id: 'payout-report.agent_username',
        defaultMessage: 'Agent Username',
    },
    casino: {
        id: 'payout-report.casino',
        defaultMessage: 'Casino',
    },
    country: {
        id: 'payout-report.country',
        defaultMessage: 'Country',
    },
    global_spins: {
        id: 'payout-report.global_spins',
        defaultMessage: 'Global Spins',
    },
    player_uid: {
        id: 'payout-report.player_uid',
        defaultMessage: 'Player ID',
    },
    player_username: {
        id: 'payout-report.player_username',
        defaultMessage: 'Player Username',
    },
    poker_cash: {
        id: 'payout-report.poker_cash',
        defaultMessage: 'Poker Cash',
    },
    poker_mtt: {
        id: 'payout-report.poker_mtt',
        defaultMessage: 'Poker MTT',
    },
    poker_plo: {
        id: 'payout-report.poker_plo',
        defaultMessage: 'Poker PLO',
    },
    sports: {
        id: 'payout-report.sports',
        defaultMessage: 'Sports',
    },
    total_share: {
        id: 'payout-report.total_share',
        defaultMessage: 'Total Share',
    },
});

export function usePayoutReportColumns(columns: PayoutReportColumns[]): DataGridColumn<PayoutReportViewModel>[] {
    const {formatNumber} = useNumberFormatter();
    const currency = 'USD';

    const agentUid: TextColumn<PayoutReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => m.agent_uid,
        field: 'agent_uid',
        title: localized.agent_uid,
        sortable: true,
    };

    const agentUsername: TextColumn<PayoutReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => m.agent_username,
        field: 'agent_username',
        title: localized.agent_username,
        sortable: true,
    };

    const casino: TextColumn<PayoutReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => formatNumber(m.casino, {style: 'currency', currency}),
        field: 'casino',
        title: localized.casino,
        sortable: true,
    };

    const country: TextColumn<PayoutReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => m.country,
        field: 'country',
        title: localized.country,
        sortable: true,
    };

    const globalSpins: TextColumn<PayoutReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => formatNumber(m.global_spins, {style: 'currency', currency}),
        field: 'global_spins',
        title: localized.global_spins,
        sortable: true,
    };

    const playerUid: TextColumn<PayoutReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => m.player_uid,
        field: 'player_uid',
        title: localized.player_uid,
        sortable: true,
    };

    const playerUsername: TextColumn<PayoutReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => m.player_username,
        field: 'player_username',
        title: localized.player_username,
        sortable: true,
    };

    const pokerCash: TextColumn<PayoutReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => formatNumber(m.poker_cash, {style: 'currency', currency}),
        field: 'poker_cash',
        title: localized.poker_cash,
        sortable: true,
    };

    const pokerMtt: TextColumn<PayoutReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => formatNumber(m.poker_mtt, {style: 'currency', currency}),
        field: 'poker_mtt',
        title: localized.poker_mtt,
        sortable: true,
    };

    const pokerPlo: TextColumn<PayoutReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => formatNumber(m.poker_plo, {style: 'currency', currency}),
        field: 'poker_plo',
        title: localized.poker_plo,
        sortable: true,
    };

    const sports: TextColumn<PayoutReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => formatNumber(m.sports, {style: 'currency', currency}),
        field: 'sports',
        title: localized.sports,
        sortable: true,
    };

    const totalShare: TextColumn<PayoutReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => formatNumber(m.total_share, {style: 'currency', currency}),
        field: 'total_share',
        title: localized.total_share,
        sortable: true,
    };

    const config: Record<PayoutReportColumns, TextColumn<PayoutReportViewModel>> = {
        agent_uid: agentUid,
        agent_username: agentUsername,
        casino,
        country,
        global_spins: globalSpins,
        player_uid: playerUid,
        player_username: playerUsername,
        poker_cash: pokerCash,
        poker_mtt: pokerMtt,
        poker_plo: pokerPlo,
        sports,
        total_share: totalShare,
    };

    return columns.map(c => config[c]);
}

type PayoutReportDataGridProps = Omit<DataGridServerProps<PayoutReportViewModel, PayoutReportViewModelKeys>, 'columns'> & {
    columns: PayoutReportColumns[];
};

export function PayoutReportDataGrid(props: PayoutReportDataGridProps) {
    const columns = usePayoutReportColumns(props.columns);
    return <DataGridServer {...props} columns={columns} />;
}
